import { RoleEnum, User } from '@sr-sdks/api-sdk-axios';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  buttonVariants,
} from '@sr/ui-library';
import { VariantProps } from 'class-variance-authority';
import { twMerge } from 'tailwind-merge';
import { AccessLevelMap } from '../constant/Common';
import { useCurrentUserAuth } from '../hooks/useCurrentUserAuth';
import { userIsMe } from '../utils/helpers/common';
import { IfFeatureEnabled } from './IfFeatureEnabled';

interface IAccessLevelDropdownProps extends VariantProps<
  typeof buttonVariants
> {
  hasMultipleWorkspaceOwner?: boolean;
  isPermittedToChangeWorkspaceOwner?: boolean;
  isReadOnly?: boolean;
  isStaffView?: boolean;
  isWorkspaceOwner?: boolean;
  onRoleSelect?: (roleKey: RoleEnum) => void;
  selectedRoleKey?: RoleEnum;
  toggleClassName?: string;
  user?: User;
}

const AccessLevelDropdown = ({
  hasMultipleWorkspaceOwner = false,
  isPermittedToChangeWorkspaceOwner = false,
  isReadOnly = false,
  isStaffView = false,
  isWorkspaceOwner,
  onRoleSelect,
  selectedRoleKey = RoleEnum.WorkspaceUser,
  user,
  variant = 'transparent',
}: IAccessLevelDropdownProps) => {
  const { currentUser } = useCurrentUserAuth();

  const isCurrentUser = user && userIsMe(user, currentUser);
  const isOnlyOwner = isWorkspaceOwner && !hasMultipleWorkspaceOwner;
  const cantModifyOwner =
    !isPermittedToChangeWorkspaceOwner &&
    selectedRoleKey === RoleEnum.WorkspaceOwner;

  if (
    // Current user can't modify their own role (except in staff view)
    (isCurrentUser && !isStaffView) ||
    // Read-only and single owner situation
    (isReadOnly && !hasMultipleWorkspaceOwner) ||
    // User is the only owner
    isOnlyOwner ||
    // User lacks permission to change owner role
    cantModifyOwner ||
    // Current user with non-owner role
    (isCurrentUser && selectedRoleKey !== RoleEnum.WorkspaceOwner)
  ) {
    return (
      <div
        className={buttonVariants({
          size: 'content',
          variant,
        })}
      >
        {AccessLevelMap[selectedRoleKey]}
        <span className="sr-only">Access Level View Only</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild data-testid="dropdown-trigger">
        <Button size="content" variant={variant}>
          {AccessLevelMap[selectedRoleKey]}
          <Icon icon="chevronDown" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <IfFeatureEnabled name="workspace.teammates.onboarding">
          {isPermittedToChangeWorkspaceOwner &&
            selectedRoleKey !== RoleEnum.WorkspaceOwner && (
              <DropdownMenuItem
                className={twMerge('!min-w-52 max-w-72 px-4 py-2 md:!max-w-96')}
                onClick={() => onRoleSelect?.(RoleEnum.WorkspaceOwner)}
              >
                <div className="grid-flow-row">
                  <p>Owner</p>
                </div>
              </DropdownMenuItem>
            )}
        </IfFeatureEnabled>

        {selectedRoleKey !== RoleEnum.WorkspaceAdmin && (
          <DropdownMenuItem
            className={twMerge('!min-w-52 max-w-72 px-4 py-2 md:!max-w-96')}
            onClick={() => onRoleSelect?.(RoleEnum.WorkspaceAdmin)}
          >
            <div className="grid-flow-row">
              <p>Admin</p>
            </div>
          </DropdownMenuItem>
        )}

        {selectedRoleKey !== RoleEnum.WorkspaceUser && (
          <DropdownMenuItem
            className={twMerge('!min-w-52 max-w-72 px-4 py-2 md:!max-w-96')}
            onClick={() => onRoleSelect?.(RoleEnum.WorkspaceUser)}
          >
            <div className="grid-flow-row">
              <p>Standard</p>
            </div>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default AccessLevelDropdown;
